/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

import 'core-js/stable';
import 'regenerator-runtime/runtime';
import '@quintoandar/cozy-core/styles/index.css';
import '@quintoandar/cozy-theme/cozy-theme/v6/latest/default/default.css';
import '@quintoandar/cozy-typeface/index.css';

// Import all the third party stuff
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import '@quintoandar/cozy-typeface';
import 'sanitize.css/sanitize.css';
import { syncHistoryWithStore } from 'react-router-redux';
import { browserHistory, Router } from 'react-router';
import { Theme, ThemeWrapper } from '@quintoandar/cozy-core/theming';
import {
  GlobalFontStyle,
  GlobalFocusVisible,
} from '@quintoandar/cozy-core/Globals';
import { detectLocale } from '@quintoandar/js-intl';
import { init } from '@quintoandar/fwp-error-monitoring';
import { FrontendObservability } from '@quintoandar/fwp-o11y-react';

import getRoutes from 'routes';

// Import root app
import App from 'containers/App';

import ReactQueryProvider from 'data/react-query/Provider';

// Import Language Provider
import LanguageProvider from 'containers/LanguageProvider';
import { getCozyThemeName } from 'Base/i18n/brand';
import { getMessages } from './i18n';

import configureStore from './configureStore';

import GlobalStyle from './global-styles';

import { polyfillIntl } from './polyfills';
import patchNodeRemoveChild from './patch';

patchNodeRemoveChild();

// Create redux store with history
const initialState = {};
const store = configureStore(initialState, browserHistory);
const MOUNT_NODE = document.getElementById('app');

const history = syncHistoryWithStore(browserHistory, store);

// Setup routes
const configuredRoutes = [
  {
    component: App,
    childRoutes: getRoutes(store),
  },
];

const render = (messages, locale) => {
  ReactDOM.render(
    <>
      <FrontendObservability
        faroUrl={process.env.GRAFANA_FARO_URL}
        faroApiKey={process.env.GRAFANA_FARO_KEY}
        appName={process.env.APP_NAME}
        environment={process.env.APP_ENVIRONMENT}
      />
      <ThemeWrapper name={Theme[getCozyThemeName(locale)]}>
        <Provider store={store}>
          <LanguageProvider messages={messages} locale={locale}>
            <ReactQueryProvider>
              <Router history={history} routes={configuredRoutes} />
            </ReactQueryProvider>
          </LanguageProvider>
        </Provider>
        <GlobalStyle />
        <GlobalFontStyle />
        <GlobalFocusVisible />
      </ThemeWrapper>
    </>,
    MOUNT_NODE,
  );
};

const locale = detectLocale();

polyfillIntl(locale).then(async () => {
  // react-intl 2 only supports 2 letter locales (languages)
  const messages = await getMessages(locale);
  render(messages, locale);
});

// Sentry setup
init({
  enabled:
    process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'forno',
  dsn: process.env.SENTRY_DSN,
  environment: process.env.SENTRY_ENVIRONMENT,
  release: process.env.SENTRY_RELEASE,
});

if (window) {
  window.BUILD_VERSION = process.env.BUILD;
}
